import { useEffect, useState } from 'react';
import { centerPerfAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';

/* 센터 실적 «기준 데이터 → 고객사 실적 센터» — 파일을 올린 실적 센터의 고객사 출고를 같은 권한 센터의 다른 실적 센터에 저장하는 예외.
 * · 목록은 서버가 정한다(/center-perf/splits/rules — 조회 범위 안 파일 실적 센터만). '자동' 은 업로드 때 최신 연도 고객사 마스터로
 *   정해 저장된 배정, '수동' 은 여기서 둔 예외다. 같은 (파일 실적 센터, 고객사명)으로 다시 저장하면 수동으로 덮어쓴다.
 * · canManage(운영·시스템 관리자·기준 데이터 관리 조직)면 추가 폼과 행마다 «예외 삭제». 배정 실적 센터 선택지는 고른 실적 센터 자신과
 *   권한 센터가 같은 실적 센터뿐이다(서버도 다시 보고, 아니면 400 문구를 그대로 보인다).
 * · 예외를 더하거나 지워도 이미 저장된 출고 행은 옮기지 않는다 — 다음 업로드부터 반영된다.
 */
const ORIGIN_LABEL = { manual: '수동', auto: '자동' };

const card = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' };
const th = { padding: '7px 9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '5px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const inp = { padding: '4px 6px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
const btn = (bg, color = '#fff') => ({ padding: '5px 10px', background: bg, color, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 });
const hint = { fontSize: '12px', color: COLORS.textMuted, marginBottom: '8px' };

const detailOf = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' && d ? d : fallback;
};

// 고른 파일 실적 센터 → 배정 선택지(자기 자신 + 권한 센터가 같은 실적 센터 · 권한 센터가 빈 센터는 자기 자신만).
export function targetsFor(centers, perfCenter) {
  const source = centers.find((c) => c.name === perfCenter);
  if (!source) return [];
  return centers.filter((c) => c.name === source.name || (!!source.access_token && c.access_token === source.access_token));
}

export default function CustomerCenterSection({ centers = [], canManage = false }) {
  const [rules, setRules] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [msg, setMsg] = useState(null);                // { tone: 'ok' | 'err', text }
  const [busy, setBusy] = useState(false);
  const [perf, setPerf] = useState('');
  const [raw, setRaw] = useState('');
  const [target, setTarget] = useState('');

  useEffect(() => {
    let alive = true;
    centerPerfAPI.splitRules()
      .then((r) => { if (alive) setRules(r.data?.rules || []); })
      .catch((e) => { if (alive) setLoadError(detailOf(e, '고객사 실적 센터 예외를 불러오지 못했습니다.')); });
    return () => { alive = false; };
  }, []);

  const refresh = async (okText) => {
    try {
      const r = await centerPerfAPI.splitRules();
      setRules(r.data?.rules || []);
      setMsg({ tone: 'ok', text: okText });
    } catch (e) {
      setMsg({ tone: 'err', text: detailOf(e, '목록을 다시 불러오지 못했습니다.') });
    }
  };

  const targets = targetsFor(centers, perf);
  const ready = !!perf && !!raw.trim() && !!target && !busy;

  const save = async () => {
    if (!ready) return;
    const body = { perf_center: perf, raw_name: raw.trim(), target_center: target };
    setBusy(true);
    setMsg(null);
    try {
      try {
        await centerPerfAPI.saveSplitRule(body);
      } catch (e) {
        setMsg({ tone: 'err', text: detailOf(e, '예외를 저장하지 못했습니다.') });
        return;
      }
      setRaw('');
      await refresh(`'${body.perf_center}' 의 '${body.raw_name}' 출고를 '${body.target_center}' 에 저장하도록 예외를 두었습니다`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    const ok = await confirmDialog(
      `'${r.perf_center}' 의 '${r.raw_name}' → '${r.target_center}' 예외를 지웁니다. 이미 저장된 출고는 그대로이고, 다음 업로드부터 반영됩니다.`,
      { danger: true },
    );
    if (!ok) return;
    setBusy(true);
    setMsg(null);
    try {
      try {
        await centerPerfAPI.removeSplitRule(r.id);
      } catch (e) {
        setMsg({ tone: 'err', text: detailOf(e, '예외를 지우지 못했습니다.') });
        return;
      }
      await refresh(`'${r.raw_name}' 예외를 지웠습니다`);
    } finally {
      setBusy(false);
    }
  };

  const headers = ['파일 실적 센터', '고객사명', '배정 실적 센터', '자동/수동', '수정 시각', ...(canManage ? [''] : [])];

  return (
    <div style={card}>
      <div style={hint}>
        파일을 올린 실적 센터의 고객사 출고를 권한 센터가 같은 다른 실적 센터에 저장합니다(예: 안양(YK) 파일의 안양 고객사).
        «자동»은 업로드 때 최신 연도 고객사 마스터로 정한 배정입니다. 예외를 바꿔도 이미 저장된 출고는 옮기지 않고 다음 업로드부터 반영됩니다.
        {canManage ? '' : ' 이 화면은 읽기 전용입니다.'}
      </div>
      {loadError && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px', marginBottom: '8px' }}>{loadError}</div>}
      {msg && (
        <div role={msg.tone === 'err' ? 'alert' : 'status'}
          style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: msg.tone === 'err' ? COLORS.dangerText : COLORS.successText }}>
          {msg.text}
        </div>
      )}

      {canManage && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
          <select aria-label="예외 실적 센터" value={perf} style={inp}
            onChange={(e) => { setPerf(e.target.value); setTarget(''); }}>
            <option value="">파일 실적 센터 선택</option>
            {centers.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <input aria-label="예외 고객사명" value={raw} maxLength={200} placeholder="고객사명(원문)" style={{ ...inp, width: '200px' }}
            onChange={(e) => setRaw(e.target.value)} />
          <select aria-label="배정 실적 센터" value={target} style={inp} disabled={!perf}
            onChange={(e) => setTarget(e.target.value)}>
            <option value="">배정 실적 센터 선택</option>
            {targets.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <button type="button" disabled={!ready} onClick={save} style={{ ...btn(COLORS.primary), opacity: ready ? 1 : 0.5 }}>예외 저장</button>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table data-testid="perf-split-rules" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>{headers.map((h, i) => <th key={h || `act-${i}`} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rules && rules.length === 0 && (
              <tr><td colSpan={headers.length} style={{ ...td, color: COLORS.textMuted }}>고객사 실적 센터 예외가 없습니다.</td></tr>
            )}
            {(rules || []).map((r) => (
              <tr key={r.id} data-rule-id={r.id} data-center={r.perf_center} data-raw={r.raw_name}>
                <td data-col="perf_center" style={{ ...td, whiteSpace: 'nowrap' }}>{r.perf_center}</td>
                <td data-col="raw_name" style={{ ...td, fontWeight: 600 }}>{r.raw_name}</td>
                <td data-col="target_center" style={{ ...td, whiteSpace: 'nowrap' }}>{r.target_center}</td>
                <td data-col="origin" style={{ ...td, whiteSpace: 'nowrap' }}>{ORIGIN_LABEL[r.origin] || r.origin}</td>
                <td data-col="updated_at" style={{ ...td, whiteSpace: 'nowrap', color: COLORS.textSub }}>
                  {fmtKstDateTime(r.updated_at || r.created_at) || '—'}
                </td>
                {canManage && (
                  <td style={td}>
                    <button type="button" aria-label={`${r.raw_name} 예외 삭제`} disabled={busy} onClick={() => remove(r)}
                      style={btn(COLORS.dangerSoft, COLORS.dangerText)}>삭제</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
